import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Plus } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/fetcher';
import {
  type WebhookCreateResponse,
  type WebhookEvent,
  createWebhook,
  fetchWebhookEvents,
  webhookEventsKey,
  webhookListKey,
} from '@/lib/queries';

/**
 * Two-step dialog: form → secret reveal. Submit transitions from step
 * 1 to step 2 with the secret in-memory; closing the dialog (or
 * clicking "I've saved it") clears the secret and resets to step 1.
 *
 * Cache: on success, we don't optimistically push the new webhook
 * into the list — instead we invalidate the list query so the
 * Webhooks page refetches and renders the canonical row.
 */

export function WebhookCreateDialog() {
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<'form' | 'reveal'>('form');
  const [created, setCreated] =
    React.useState<WebhookCreateResponse | null>(null);

  const [url, setUrl] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [events, setEvents] = React.useState<WebhookEvent[]>([]);
  const [copied, setCopied] = React.useState(false);

  // Brief 131 — live event registry from GET /v1/webhooks/events. Stable
  // across a session → long stale time. Mirrors WebhookList's inline
  // useQuery shape (queryKey + queryFn from queries.ts).
  const eventsQuery = useQuery({
    queryKey: webhookEventsKey,
    queryFn: fetchWebhookEvents,
    staleTime: 5 * 60 * 1000,
  });
  const availableEvents = eventsQuery.data ?? [];

  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: createWebhook,
    onSuccess: (res) => {
      setCreated(res);
      setStep('reveal');
      void qc.invalidateQueries({ queryKey: webhookListKey });
    },
  });

  const reset = () => {
    setUrl('');
    setDescription('');
    setEvents([]);
    setStep('form');
    setCreated(null);
    setCopied(false);
    create.reset();
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (events.length === 0) return;
    create.mutate({
      url,
      events,
      ...(description ? { description } : {}),
    });
  };

  const onToggleEvent = (event: WebhookEvent) => {
    setEvents((prev) =>
      prev.includes(event)
        ? prev.filter((e) => e !== event)
        : [...prev, event],
    );
  };

  const copySecret = async () => {
    if (!created?.secret) return;
    try {
      await navigator.clipboard.writeText(created.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          New webhook
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[520px]">
        {step === 'form' ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Register a webhook</DialogTitle>
              <DialogDescription>
                Relaystation POSTs each subscribed event to your URL. The
                signing secret will be shown ONCE on the next screen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com/webhooks/relaystation"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Events</Label>
              {eventsQuery.isLoading ? (
                <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                  Loading available event types…
                </div>
              ) : availableEvents.length === 0 ? (
                <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                  No event types are registered yet. Webhooks will activate
                  once your products fire their first event.
                </div>
              ) : (
                <>
                  <div className="grid gap-2 rounded-md border p-3 text-sm">
                    {availableEvents.map((event) => (
                      <label
                        key={event}
                        className="flex items-center gap-2 font-mono text-xs"
                      >
                        <input
                          type="checkbox"
                          checked={events.includes(event)}
                          onChange={() => onToggleEvent(event)}
                          className="h-4 w-4 rounded border-input"
                        />
                        {event}
                      </label>
                    ))}
                  </div>
                  {events.length === 0 ? (
                    <p className="text-xs text-destructive">
                      Pick at least one event.
                    </p>
                  ) : null}
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                placeholder="Production webhook for SaaS App"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
              />
            </div>
            {create.error ? (
              <Alert variant="destructive">
                <AlertTitle>
                  Couldn't create webhook (
                  {create.error instanceof ApiError
                    ? create.error.code
                    : 'unknown'}
                  )
                </AlertTitle>
                <AlertDescription>
                  {create.error instanceof Error
                    ? create.error.message
                    : 'Try again.'}
                </AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  create.isPending || !url || events.length === 0
                }
              >
                {create.isPending ? 'Creating…' : 'Create webhook'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                Save your signing secret
              </DialogTitle>
              <DialogDescription>
                We won't show this again. Copy it now — your webhook
                consumer will use it to verify the HMAC of each payload.
              </DialogDescription>
            </DialogHeader>
            <Alert>
              <AlertTitle>One-time secret</AlertTitle>
              <AlertDescription>
                Store this in a secret manager. If you lose it, you'll
                need to delete this webhook and create a new one.
              </AlertDescription>
            </Alert>
            <div className="rounded-md border bg-muted/50 p-3">
              <code className="block break-all font-mono text-xs">
                {created?.secret}
              </code>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={copySecret} className="gap-2">
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
              <Button onClick={() => handleOpenChange(false)}>
                I've saved it
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
